import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography, Chip, Button } from '@mui/material';
import { LocationOn, Room } from '@mui/icons-material';
import { geocodeTrainingRoom } from '../components/funtionsBD/Mainservice';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icons
const createCustomIcon = (color = '#1976d2') => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 30px;
      height: 30px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 16px;
    ">🏓</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15]
  });
};

// Component to fit map bounds to markers
function FitBounds({ trainingRooms, luxembourgBounds }) {
  const map = useMap();
  
  useEffect(() => {
    if (map) {
      try {
        // Add a longer delay to ensure map is fully initialized
        const timeoutId = setTimeout(() => {
          try {
            // Check if map is still valid and has the necessary methods
            if (map && typeof map.fitBounds === 'function' && map.getContainer()) {
              if (trainingRooms.length > 0) {
                const bounds = trainingRooms
                  .filter(room => room.troom_lat && room.troom_lon)
                  .map(room => [parseFloat(room.troom_lat), parseFloat(room.troom_lon)]);
                
                if (bounds.length > 0) {
                  // Fit to training rooms with padding
                  map.fitBounds(bounds, { padding: [30, 30] });
                } else {
                  // If no training rooms, show Luxembourg bounds
                  map.fitBounds(luxembourgBounds, { padding: [20, 20] });
                }
              } else {
                // Show Luxembourg bounds by default
                map.fitBounds(luxembourgBounds, { padding: [20, 20] });
              }
            }
          } catch (error) {
            console.warn('Error fitting bounds:', error);
          }
        }, 500);
        
        return () => clearTimeout(timeoutId);
      } catch (error) {
        console.warn('Error processing bounds:', error);
      }
    }
  }, [trainingRooms, map, luxembourgBounds]);
  
  return null;
}

const TrainingRoomsMap = ({ trainingRooms, onGeocodeRoom }) => {
  const [geocodingRooms, setGeocodingRooms] = useState(new Set());
  const [mapKey, setMapKey] = useState(0);
  const [mapError, setMapError] = useState(null);

  // Luxembourg center coordinates and bounds
  const luxembourgCenter = [49.8153, 6.1296];
  const luxembourgBounds = [
    [49.4, 5.7], // Southwest corner
    [50.2, 6.6]  // Northeast corner
  ];

  // Force map re-render when training rooms change
  useEffect(() => {
    setMapKey(prev => prev + 1);
    setMapError(null);
  }, [trainingRooms]);

  const handleGeocodeRoom = async (roomId, roomName) => {
    setGeocodingRooms(prev => new Set([...prev, roomId]));
    
    try {
      const result = await geocodeTrainingRoom(roomId);
      if (result && result.status === 'success') {
        // Refresh the training rooms data
        if (onGeocodeRoom) {
          onGeocodeRoom(roomId, result.data);
        }
      }
    } catch (error) {
      console.error('Geocoding failed:', error);
    } finally {
      setGeocodingRooms(prev => {
        const newSet = new Set(prev);
        newSet.delete(roomId);
        return newSet;
      });
    }
  };

  const roomsWithCoordinates = trainingRooms.filter(room => 
    room.troom_lat && room.troom_lon && 
    !isNaN(parseFloat(room.troom_lat)) && 
    !isNaN(parseFloat(room.troom_lon))
  );

  const roomsWithoutCoordinates = trainingRooms.filter(room => 
    !room.troom_lat || !room.troom_lon || 
    isNaN(parseFloat(room.troom_lat)) || 
    isNaN(parseFloat(room.troom_lon))
  );

  // Show a message if no training rooms have coordinates
  if (roomsWithCoordinates.length === 0) {
    return (
      <Box sx={{ 
        width: '100%', 
        height: '500px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        backgroundColor: '#f5f5f5',
        borderRadius: 2
      }}>
        <Typography variant="h6" color="text.secondary">
          No training rooms with coordinates found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Use the geocoding buttons to add coordinates to training rooms
        </Typography>
      </Box>
    );
  }

  // Show error message if map failed to load
  if (mapError) {
    return (
      <Box sx={{ 
        width: '100%', 
        height: '500px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        backgroundColor: '#ffebee',
        borderRadius: 2,
        border: '1px solid #f44336'
      }}>
        <Typography variant="h6" color="error">
          Map failed to load
        </Typography>
        <Typography variant="body2" color="error">
          {mapError}
        </Typography>
        <Button 
          variant="outlined" 
          color="error"
          onClick={() => {
            setMapError(null);
            setMapKey(prev => prev + 1);
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  try {
    return (
      <Box sx={{ width: '100%', height: '500px', position: 'relative' }}>
        <MapContainer
          key={`map-${mapKey}`}
          center={luxembourgCenter}
          zoom={7}
          minZoom={6}
          maxZoom={15}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          whenReady={() => {
            console.log('Map is ready');
            setMapError(null);
          }}
          zoomControl={true}
          attributionControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <FitBounds trainingRooms={roomsWithCoordinates} luxembourgBounds={luxembourgBounds} />
          
          {roomsWithCoordinates.map((room, index) => {
            try {
              const lat = parseFloat(room.troom_lat);
              const lon = parseFloat(room.troom_lon);
              
              // Validate coordinates
              if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
                console.warn(`Invalid coordinates for room ${room.troom_name}:`, { lat, lon });
                return null;
              }
              
              return (
                <Marker
                  key={`marker-${room.troom_id}-${index}`}
                  position={[lat, lon]}
                  icon={createCustomIcon('#1976d2')}
                >
                <Popup>
                  <Box sx={{ minWidth: 250 }}>
                    <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Room fontSize="small" />
                      {room.troom_name}
                    </Typography>
                    {room.troom_address ? (
                      <Typography
                        component="a"
                        href={`https://www.google.com/maps/search/?api=1&query=${lat},${lon}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        color="primary"
                        sx={{ mb: 1.5, display: 'block', textDecoration: 'underline' }}
                      >
                        {room.troom_address}
                      </Typography>
                    ) : null}
                    
                    {/* Coordinates Display */}
                    <Box sx={{ 
                      mb: 1.5, 
                      p: 1, 
                      backgroundColor: '#f5f5f5', 
                      borderRadius: 1,
                      border: '1px solid #e0e0e0'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                        Coordinates:
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        Lat: {lat.toFixed(6)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        Lon: {lon.toFixed(6)}
                      </Typography>
                    </Box>
                    
                    <Chip 
                      label="📍 Located" 
                      size="small" 
                      color="success" 
                      icon={<LocationOn />}
                    />
                  </Box>
                </Popup>
                </Marker>
              );
            } catch (error) {
              console.warn(`Error rendering marker for room ${room.troom_name}:`, error);
              return null;
            }
          })}
        </MapContainer>
      
      {/* Legend and controls */}
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        right: 10, 
        background: 'white', 
        padding: 2, 
        borderRadius: 2, 
        boxShadow: 2,
        minWidth: 200
      }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Training Rooms Map
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
          Luxembourg Area View
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#1976d2', borderRadius: '50%' }} />
            {roomsWithCoordinates.length} rooms with coordinates
          </Typography>
          
          <Typography variant="body2" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#ff9800', borderRadius: '50%' }} />
            {roomsWithoutCoordinates.length} rooms need geocoding
          </Typography>
        </Box>
        
        {roomsWithoutCoordinates.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Rooms needing coordinates:
            </Typography>
            {roomsWithoutCoordinates.slice(0, 3).map(room => (
              <Box key={room.troom_id} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {room.troom_name}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={geocodingRooms.has(room.troom_id)}
                  onClick={() => handleGeocodeRoom(room.troom_id, room.troom_name)}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  {geocodingRooms.has(room.troom_id) ? '⏳' : '📍'}
                </Button>
              </Box>
            ))}
            {roomsWithoutCoordinates.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{roomsWithoutCoordinates.length - 3} more...
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
    );
  } catch (error) {
    console.error('Error rendering map:', error);
    setMapError(error.message);
    return (
      <Box sx={{ 
        width: '100%', 
        height: '500px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        backgroundColor: '#ffebee',
        borderRadius: 2,
        border: '1px solid #f44336'
      }}>
        <Typography variant="h6" color="error">
          Map failed to load
        </Typography>
        <Typography variant="body2" color="error">
          {error.message}
        </Typography>
        <Button 
          variant="outlined" 
          color="error"
          onClick={() => {
            setMapError(null);
            setMapKey(prev => prev + 1);
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }
};

export default TrainingRoomsMap;


